// The whales of the client (ui-map-interface.md §6.4, R499, R525): dark
// silhouettes that drift through the water under the terrain.
//
// This layer and `kraken.ts` are the only purely client-side layers in the
// game. Nothing here is simulated, nothing here can be interacted with, and
// no two players see the same whale. It is set dressing, and the server has
// better things to do.
//
// The motion is `render/creatures.ts:85-96` line for line — the wander, the
// bob, the roll and the wrap. That module builds its whale with PixiJS, so
// this file imports nothing from it (`fast/nopixi.test.ts`).
//
// **The whales stay UNDER the terrain, and the kraken does not.** R522
// measured the occlusion the creature slot pays: the terrain program paints
// the trench floor over this layer at α 0.88. A whale must take it, because
// §6.4 refuses a whale that swims in front of rock; a kraken lives in a
// trench and meets no ridge, so R525 gave it a slot of its own above the
// terrain and moved its clocks and its drawing to `kraken.ts`.
//
// The whale count follows the world area (R479). It comes from the leaf
// `render/ambience-counts.ts`, which both the map and the menu previews
// read, so neither answer may drift from the other. The `whalen` knob
// multiplies it and the floor of one whale stays: a multiplier trims the
// crowd, it does not empty the sea.

import { WORLD, WORLD_H, mulberry32 } from '../../world';
import { whaleCount } from '../../render/ambience-counts';
import { CREATURE_WHALE_TINT } from '../../gen/presentation';
import { outside, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type { AtlasEntry } from '../gfx/atlas';
import type { BatchSource, QuadSink } from './batches';

/**
 * Dark silhouette, barely there (§6.4). The atlas cell is white so a layer
 * can tint it, and a white whale would be the brightest thing on the map.
 * Both figures are `render/creatures.ts:56-57`. The ink is
 * `creatures.whale_tint`, a shade of the paper in the light identity
 * (R3296).
 */
const WHALE_TINT = CREATURE_WHALE_TINT;
const WHALE_ALPHA = 0.34;

/** How many floats one whale takes: x, y, heading, speed, scale, bob. */
const WHALE_STRIDE = 6;

export class CreatureLayer implements Layer {
  readonly name = 'creatures';

  private readonly quads: QuadSink;
  private readonly cell: AtlasEntry;
  private shown = true;

  /** x, y, heading, speed, scale, bob for each whale, in one flat pool. */
  private pool = new Float32Array(0);
  private whales = 0;

  private whalesOn = true;
  private whalen = 1;
  private rand = mulberry32(0x77a1e);

  constructor(src: BatchSource) {
    this.quads = src.newQuads(8);
    this.cell = src.sprite('whale');
    this.seed();
  }

  /**
   * The blocked mask of the running world.
   *
   * The school reads the live grid for its count and its scatter, so the
   * seed must run again after the hello has set it (R479). The mask itself
   * is the kraken's business, and that layer keeps it now (R525).
   */
  setBlocked(_mask: Uint8Array | null): void {
    this.seed();
  }

  /**
   * The `whales` and `whalen` knobs (`fast/knobs.ts`).
   *
   * A change of the count seeds the school again, so the world keeps one
   * deterministic scatter for one set of knobs. Nothing happens while the
   * knobs stand, so a `hashchange` that names another piece costs nothing.
   */
  configure(whales: boolean, whalen: number): void {
    if (whales === this.whalesOn && whalen === this.whalen) return;
    this.whalesOn = whales;
    this.whalen = whalen;
    this.seed();
  }

  update(frame: FrameInfo): void {
    this.packWhales(frame, frame.dtMs / 1000);
  }

  draw(ctx: DrawCtx): void {
    this.quads.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  /** How many whales the world holds. The tests read it. */
  get count(): number { return this.whales; }

  // -- the school ------------------------------------------------------------

  /**
   * Scatters the school.
   *
   * The generator starts again from the layer's seed, so one grid and one
   * set of knobs always give one school. The draw order of the six values
   * is the PixiJS constructor's own (`render/creatures.ts:58-65`), which
   * keeps the core world's whales where they have always been.
   */
  private seed(): void {
    this.rand = mulberry32(0x77a1e);
    this.whales = this.whalesOn ? Math.max(1, Math.round(whaleCount() * this.whalen)) : 0;
    if (this.pool.length < this.whales * WHALE_STRIDE) {
      this.pool = new Float32Array(this.whales * WHALE_STRIDE);
    }
    const p = this.pool;
    for (let i = 0; i < this.whales; i++) {
      const at = i * WHALE_STRIDE;
      const scale = 1.6 + this.rand() * 1.2;   // render/creatures.ts:58
      p[at] = this.rand() * WORLD;
      p[at + 1] = this.rand() * WORLD_H;
      p[at + 2] = this.rand() * Math.PI * 2;
      p[at + 3] = 9 + this.rand() * 7;         // render/creatures.ts:64
      p[at + 4] = scale;
      p[at + 5] = this.rand() * Math.PI * 2;
    }
  }

  /**
   * Advances and packs the school in one pass.
   *
   * A whale outside the rect still swims, because the sea off screen must
   * not stop and then jump, but it packs no instance. The rect is grown by
   * the widest whale's half length as well as the frame's own margin: the
   * mark is longer than `CULL_MARGIN_PX`, and a tail cut off at the edge of
   * the screen would be worse than the quad it saves.
   */
  private packWhales(frame: FrameInfo, dt: number): void {
    const q = this.quads;
    q.begin();
    if (this.whales === 0) return;
    const pad = this.cell.w * 1.4;             // half of the widest scale, 2.8
    const c = frame.cull;
    const p = this.pool;
    for (let i = 0; i < this.whales; i++) {
      const at = i * WHALE_STRIDE;
      // A slow heading wander, a vertical bob, and a tail-swing roll.
      const heading = p[at + 2] + (this.rand() - 0.5) * 0.4 * dt;
      let x = p[at] + Math.cos(heading) * p[at + 3] * dt;
      let y = p[at + 1] + Math.sin(heading) * p[at + 3] * dt;
      // The whales wrap on the world rectangle (R395), so a wide map does
      // not park them all in a band above an empty bottom half.
      x = (x + WORLD) % WORLD;
      y = (y + WORLD_H) % WORLD_H;
      p[at] = x; p[at + 1] = y; p[at + 2] = heading;
      const bob = p[at + 5];
      const dy = Math.sin(frame.timeS * 0.6 + bob) * 6;        // render/creatures.ts:94
      if (outside(x, y + dy, c.x0 - pad, c.y0 - pad, c.x1 + pad, c.y1 + pad)) continue;
      const rot = heading + Math.sin(frame.timeS * 1.3 + bob) * 0.06; // :95
      q.sprite(x, y + dy, rot, this.cell, WHALE_TINT, WHALE_ALPHA, p[at + 4], 0, true);
    }
  }
}
